import { isAfter } from 'tio-common/utils/date/is-after';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import Service from '@ember/service';
import ENV from 'tio-employee/config/environment';
import { getSubdomain, getPartnerLogoUrl, isLocalhost } from 'tio-common/utils/hostname';
import type CompanyModel from 'tio-data/models/company';
import type CompanySettingModel from 'tio-data/models/company-setting';
import type ConditionModel from 'tio-data/models/condition';
import type PartnerService from './partner';
import type SessionContextService from './session-context';
// This is for types. Anything we have custom may show an error.
import type StoreService from 'ember-data/store';

// Re-export for backwards compatibility if needed elsewhere
export type { HostnameParts } from 'tio-common/utils/hostname';

export type PreRegisterSettings = Partial<CompanySettingModel>;
export type PreRegisterCompany = Partial<CompanyModel>;

export default class PreRegisterService extends Service {
  @service declare sessionContext: SessionContextService;
  @service declare store: StoreService;
  @service declare partner: PartnerService;

  @tracked sparseCompany?: PreRegisterCompany;
  @tracked conditions: ConditionModel[] = [];

  /**
   * Gets the subdomain from the current hostname.
   * Uses shared hostname utility for consistency across services.
   */
  get subdomain() {
    return getSubdomain();
  }

  get settings() {
    // @ts-expect-error seems like a bug
    return this.sparseCompany?.settings;
  }

  get companyName() {
    return this.sparseCompany?.name;
  }

  get programTitle() {
    return this.sparseCompany?.programTitle;
  }

  get logo() {
    return getPartnerLogoUrl(this.subdomain, ENV.tioEnvironment);
  }

  get termsOfUse() {
    return this.conditions.find((condition) => condition.classification === 'app_terms_of_use');
  }

  get privacyPolicy() {
    return this.conditions.find((condition) => condition.classification === 'app_privacy_policy');
  }

  get customTerms() {
    return this.conditions.filter((condition) => condition.classification === 'custom');
  }

  get isEmployeeSubdomain() {
    return this.subdomain === 'employee' || this.subdomain === '';
  }

  /**
   * Checks if the app is running on localhost.
   * Exposed as a service property to allow mocking in tests.
   */
  get isLocalhost() {
    return isLocalhost();
  }

  /**
   * Determines if the email lookup should be shown.
   * Combines isEmployeeSubdomain and isLocalhost checks.
   */
  get shouldShowEmailLookup() {
    return this.isEmployeeSubdomain && !this.isLocalhost;
  }

  get securitySettings() {
    return this.settings?.security;
  }

  get launchDate() {
    return this.sparseCompany?.launchDate ?? '';
  }

  get isLaunched() {
    return this?.launchDate ? isAfter(new Date(), this.launchDate) : true;
  }

  get daysUntilLaunch() {
    if (!this.launchDate || this.isLaunched) return 0;

    const targetDate = new Date(this.launchDate);
    const currentDate = new Date();
    const timeDifference = targetDate.getTime() - currentDate.getTime();
    const daysDifference = Math.ceil(timeDifference / (1000 * 60 * 60 * 24));

    return daysDifference;
  }

  emailLookup = async (email: string) => {
    // @ts-expect-error - Ember Data adapter property not typed on MinimumAdapterInterface
    const { host } = this.store.adapterFor('application');

    const response = await fetch(`${host}/preregister/validate-email`, {
      method: 'POST',
      body: JSON.stringify({ email }),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return await response.json();
  };

  load = async () => {
    // @ts-expect-error - Ember Data adapter properties not typed on MinimumAdapterInterface
    const { headers, host } = this.store.adapterFor('application');
    const subdomain = this.subdomain || 'employee';
    try {
      const response = await fetch(`${host}/preregister/${subdomain}`, {
        headers,
      });
      if (response.ok) {
        this.sparseCompany = await response.json();
      }
    } catch (error) {
      console.warn(`Failed to load preregister data from ${host}/preregister/${subdomain}`, error);
      this.sparseCompany = undefined;
      this.conditions = [];
      return;
    }

    await this.loadTermsAndConditions();
  };

  loadTermsAndConditions = async () => {
    // @ts-expect-error - Ember Data adapter properties not typed on MinimumAdapterInterface
    const { headers, host } = this.store.adapterFor('application');
    const subdomain = this.subdomain || 'employee';

    try {
      const conditionResponse = await fetch(`${host}/preregister/${subdomain}/conditions`, {
        headers,
      });
      if (conditionResponse.ok) {
        const payload = await conditionResponse.json();
        // @ts-expect-error - Ember Data pushPayload expects 2 arguments after rollback
        this.store.pushPayload(payload);
        const models = payload?.data?.map((record: Record<string, unknown>) => {
          // @ts-expect-error: fix model typing
          return this.store.peekRecord<ConditionModel>('condition', record.id);
        });
        this.conditions = models || [];
      }
    } catch (error) {
      console.warn(
        `Failed to load preregister conditions from ${host}/preregister/${subdomain}/conditions`,
        error
      );
      this.conditions = [];
    }
  };
}
